import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import AdminKpiRecordService from './AdminKpiRecordService';

// Base-salary runs (mid-month and end-month). KPI, OT, bonus, mistakes and
// unpaid leave are paid on the 5th, so they live in FifthRunBreakdown.
//
// The halves match the backend's splitHalf(): floor first, remainder second, so
// the two payments always add back to the exact full amount.
const PaymentBreakdown = ({ admin, isMidMonth, paymentFrequency }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const salary = admin?.salary || 0;
  const positionAllowance = admin?.positionAllowance || 0;
  const socialSecurity = admin?.socialSecurityAmount || 0;
  const studentLoan = admin?.studentLoan || 0;

  const firstHalf = (n) => Math.floor(n / 2);
  const secondHalf = (n) => n - Math.floor(n / 2);

  if (isMidMonth && paymentFrequency === 'once') {
    return null; // a 'once' admin has no mid-month run
  }

  const half = isMidMonth ? firstHalf : secondHalf;
  const isOnce = paymentFrequency === 'once';

  // 'once' pays the whole thing at end-month; 'twice' splits it.
  const lines = isOnce
    ? [
        ['Salary', salary, '+'],
        ['Position', positionAllowance, '+'],
        ['Social security', socialSecurity, '-'],
        ['Student loan', studentLoan, '-'],
      ]
    : [
        ['Salary ½', half(salary), '+'],
        ['Position ½', half(positionAllowance), '+'],
        ['Social security ½', half(socialSecurity), '-'],
        ...(isMidMonth ? [] : [['Student loan', studentLoan, '-']]),
      ];

  const net = lines.reduce((sum, [, value, sign]) => sum + (sign === '-' ? -value : value), 0);

  return (
    <div className="border border-gray-200 rounded p-1.5 text-xs">
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-xs">{isMidMonth ? 'Mid' : 'End'}</span>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-indigo-600 hover:text-indigo-800"
        >
          {isExpanded ? '−' : '+'}
        </button>
      </div>

      <div className="bg-blue-50 p-1 rounded">
        {isExpanded && (
          <div className="text-xs space-y-0.5 mb-1">
            {lines.map(([label, value, sign]) => (
              <div
                key={label}
                className={`flex justify-between ${value === 0 ? 'text-gray-400' : sign === '-' ? 'text-red-600' : ''}`}
              >
                <span>{label}:</span>
                <span>{sign}{value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
        <div className="text-sm font-bold text-blue-900">{net.toLocaleString()}</div>
      </div>
    </div>
  );
};

// The 5th run, paid on the 5th of the following month. Everything here is frozen
// on the summary at generation time, so this is a read of stored values, not a
// recalculation in the browser.
const FifthRunBreakdown = ({ summary }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const kpiIncentive = summary?.salaryIncrease || 0;
  const kpiDeduction = summary?.deductionAmount || 0;
  const overtime = summary?.overtimeAmount || 0;
  const bonus = summary?.actualBonus || 0;
  const mistakes = summary?.mistakeDeduction || 0;
  const unpaidLeave = summary?.unpaidLeaveDeduction || 0;
  const net = summary?.run3NetPay || 0;

  const lines = [
    ['KPI incentive', kpiIncentive, '+'],
    ['KPI deduct', kpiDeduction, '-'],
    ['OT', overtime, '+'],
    ['Bonus', bonus, '+'],
    ['Mistakes', mistakes, '-'],
    ['Unpaid leave', unpaidLeave, '-'],
  ];

  // A hand-edited total is what gets paid, so the parts above can legitimately
  // disagree with it. Say so rather than showing a breakdown that doesn't add up.
  const partsTotal = kpiIncentive - kpiDeduction + overtime + bonus - mistakes - unpaidLeave;
  const editedAway = summary?.isManuallyEdited && partsTotal !== net;

  return (
    <div className="border border-gray-200 rounded p-1.5 text-xs">
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-xs">5th</span>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-indigo-600 hover:text-indigo-800"
        >
          {isExpanded ? '−' : '+'}
        </button>
      </div>

      <div className="bg-purple-50 p-1 rounded">
        {isExpanded && (
          <div className="text-xs space-y-0.5 mb-1">
            {lines.map(([label, value, sign]) => (
              <div
                key={label}
                className={`flex justify-between ${value === 0 ? 'text-gray-400' : sign === '-' ? 'text-red-600' : 'text-green-600'}`}
              >
                <span>{label}:</span>
                <span>{sign}{value.toLocaleString()}</span>
              </div>
            ))}
            {editedAway && (
              <div className="text-amber-700 pt-0.5 border-t border-purple-200">
                Total set by hand ({partsTotal.toLocaleString()} computed)
              </div>
            )}
          </div>
        )}
        <div className="text-sm font-bold text-purple-900">{net.toLocaleString()}</div>
      </div>
    </div>
  );
};

const baht = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());

// Automatic, or hand-set. Paying is recorded on the Salary Payment page, not here.
const summaryState = (summary) => (summary.isManuallyEdited ? 'manual' : 'auto');

const StateBadge = ({ state }) => {
  const style = {
    auto: 'bg-gray-100 text-gray-700',
    manual: 'bg-amber-100 text-amber-800',
  }[state];
  const label = { auto: 'Auto', manual: 'Edited' }[state];
  return <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${style}`}>{label}</span>;
};

// The run totals the backend froze at generation time — what actually gets paid.
const FrozenRuns = ({ summary }) => {
  const total = [summary.run1NetPay, summary.run2NetPay, summary.run3NetPay]
    .reduce((sum, v) => sum + (Number(v) || 0), 0);

  return (
    <div className="text-xs space-y-0.5">
      <div className="flex justify-between gap-2"><span className="text-gray-500">Run 1</span><span>{baht(summary.run1NetPay)}</span></div>
      <div className="flex justify-between gap-2"><span className="text-gray-500">Run 2</span><span>{baht(summary.run2NetPay)}</span></div>
      <div className="flex justify-between gap-2"><span className="text-gray-500">Run 3</span><span>{baht(summary.run3NetPay)}</span></div>
      <div className="flex justify-between gap-2 border-t border-gray-200 pt-0.5 font-bold">
        <span>Total</span><span className="text-indigo-600">{baht(total)}</span>
      </div>
    </div>
  );
};

// Totals-only editing: Run 1 and Run 2 have no stored components, and an edited
// Run-3 total is what gets paid regardless of what the components add up to.
const ManualEditModal = ({ summary, onCancel, onSave, isSaving }) => {
  const [values, setValues] = useState({
    run1NetPay: summary.run1NetPay ?? 0,
    run2NetPay: summary.run2NetPay ?? 0,
    run3NetPay: summary.run3NetPay ?? 0,
  });

  const fields = [
    ['run1NetPay', 'Run 1 — mid-month'],
    ['run2NetPay', 'Run 2 — end-month'],
    ['run3NetPay', 'Run 3 — 5th of next month'],
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <h3 className="text-lg font-semibold mb-1">
          Edit run totals — {summary.admin?.nickname || `Admin #${summary.adminId}`}
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Typing a total marks this row as edited, so generating the month will skip it.
          Recalculate to go back to automatic.
        </p>

        {fields.map(([key, label]) => (
          <div key={key} className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <input
              type="number"
              value={values[key]}
              onChange={(e) => setValues({ ...values, [key]: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={isSaving}
            />
          </div>
        ))}

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(values)}
            disabled={isSaving}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save totals'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminKpiSummaryGenerate = () => {
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [maxBonus, setMaxBonus] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [summaries, setSummaries] = useState([]);
  const [isLoadingSummaries, setIsLoadingSummaries] = useState(false);
  const [skipped, setSkipped] = useState([]);
  const [editingSummary, setEditingSummary] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Generate year options (starting from 2026)
  const currentYear = new Date().getFullYear();
  const startYear = 2026;
  const yearOptions = [];
  for (let y = currentYear; y >= startYear; y--) {
    yearOptions.push(y);
  }

  // Load summaries when month/year changes
  useEffect(() => {
    if (month && year) {
      loadSummaries();
    } else {
      setSummaries([]);
    }
  }, [month, year]);

  const loadSummaries = async () => {
    if (!month || !year) return;
    
    setIsLoadingSummaries(true);
    try {
      const response = await AdminKpiRecordService.getMonthlySummaries({ month, year });
      setSummaries(response.data);
    } catch (error) {
      console.error('Error loading summaries:', error);
      setSummaries([]);
    } finally {
      setIsLoadingSummaries(false);
    }
  };

  const handleGenerate = async () => {
    if (!month || !year) {
      toast.error('Please select both month and year');
      return;
    }
    if (!maxBonus || maxBonus <= 0) {
      toast.error('Please enter max bonus amount');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await AdminKpiRecordService.generateMonthlySummary(month, year, maxBonus);
      toast.success(response.data.message || 'Monthly summary generated successfully');
      // Rows that were hand-edited are left alone — show which, so they
      // can be recalculated one by one instead of silently going stale.
      const untouched = response.data.skipped || [];
      setSkipped(untouched);
      if (untouched.length > 0) {
        toast.info(`${untouched.length} row(s) left untouched — see the list below`);
      }
      // Reload summaries after generation
      await loadSummaries();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate monthly summary';
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  // One helper for all three row actions: they differ only in the call and the wording.
  const runRowAction = async (summaryId, action, successMessage) => {
    setBusyId(summaryId);
    try {
      await action();
      toast.success(successMessage);
      setSkipped((prev) => prev.filter((s) => s.summaryId !== summaryId));
      await loadSummaries();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleRecalculate = (summary) =>
    runRowAction(
      summary.id,
      () => AdminKpiRecordService.recalculateMonthlySummary(summary.id),
      'Recalculated from current records'
    );

  const handleSaveManual = async (values) => {
    const summary = editingSummary;
    await runRowAction(
      summary.id,
      () => AdminKpiRecordService.setMonthlySummaryManual(summary.id, values),
      'Run totals saved'
    );
    setEditingSummary(null);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-4">
        <h2 className='text-5xl mb-4'>Generate Monthly KPI Summary</h2>
        
        <div className="bg-white p-6 rounded-lg shadow max-w-2xl">
          <p className="text-gray-600 mb-6">
            Generate or update monthly KPI summaries for all admins. This will calculate final points, 
            deduction amounts, salary increases, and net salary adjustments based on KPI records.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Month
              </label>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isGenerating}
              >
                <option value="">Select Month</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                  <option key={m} value={m}>
                    {new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Year
              </label>
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isGenerating}
              >
                <option value="">Select Year</option>
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Bonus (THB)
              </label>
              <input
                type="number"
                value={maxBonus}
                onChange={(e) => setMaxBonus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., 5000"
                disabled={isGenerating}
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Actual bonus = min(working hours / 60, 1) × max bonus
              </p>
            </div>
          </div>

          <button
            onClick={handleGenerate}
            disabled={isGenerating || !month || !year || !maxBonus || maxBonus <= 0}
            className="w-full md:w-auto px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? 'Generating...' : 'Generate Summary'}
          </button>
        </div>

        {/* Rows the last generate deliberately left alone */}
        {skipped.length > 0 && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-amber-900 mb-2">
              Left untouched by the last generate ({skipped.length})
            </h3>
            <p className="text-xs text-amber-800 mb-3">
              Hand-edited rows are never overwritten. Recalculate one to drop its edit and bring it
              back in line with the current records. Runs already paid keep their amount either way.
            </p>
            <ul className="space-y-1">
              {skipped.map((s) => (
                <li key={s.summaryId} className="flex items-center gap-3 text-sm">
                  <span className="font-medium">{s.adminName}</span>
                  <StateBadge state="manual" />
                  <button
                    onClick={() => handleRecalculate({ id: s.summaryId })}
                    disabled={busyId === s.summaryId}
                    className="text-xs text-indigo-600 hover:text-indigo-800 disabled:text-gray-400"
                  >
                    Recalculate
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Display Payment Calculations */}
        {month && year && (
          <div className="mt-6 bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">
                Payment Calculation for {new Date(2000, parseInt(month) - 1).toLocaleString('default', { month: 'long' })} {year}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                "Payroll (frozen)" is what the backend stored for this month, including any hand-set
                total. The Mid / End / 5th columns break down where those figures come from.
              </p>
            </div>
            
            {isLoadingSummaries ? (
              <div className="p-6 text-center text-gray-500">Loading summaries...</div>
            ) : summaries.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No summaries found for this month. Click "Generate Summary" to create them.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase sticky left-0 bg-gray-50 z-10">
                        Admin
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Points
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Status
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Payroll (frozen)
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Mid-Month
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase">
                        End-Month
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase">
                        5th Run
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Bank
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {summaries.map((summary) => {
                      const admin = summary.admin;
                      const paymentFrequency = admin?.paymentFrequency || 'twice';
                      const finalPoints = parseFloat(summary.finalPoints);
                      
                      return (
                        <tr key={summary.id} className="hover:bg-gray-50">
                          <td className="py-2 px-3 text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">
                            <div>{admin?.nickname || `Admin #${summary.adminId}`}</div>
                            <div className="text-xs text-gray-500">
                              {paymentFrequency === 'twice' ? '2x/month' : '1x/month'}
                            </div>
                          </td>
                          <td className="py-2 px-3 text-sm">
                            <span className={`font-semibold ${
                              finalPoints >= 95 ? 'text-green-600' : 
                              finalPoints >= 90 ? 'text-yellow-600' : 
                              'text-red-600'
                            }`}>
                              {finalPoints.toFixed(2)}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-sm">
                            <StateBadge state={summaryState(summary)} />
                          </td>
                          <td className="py-2 px-3 text-sm min-w-[9rem]">
                            <FrozenRuns summary={summary} />
                          </td>
                          <td className="py-2 px-3 text-sm">
                            {paymentFrequency === 'twice' ? (
                              <PaymentBreakdown
                                admin={admin}
                                isMidMonth={true}
                                paymentFrequency={paymentFrequency}
                              />
                            ) : (
                              <span className="text-gray-400 text-xs">N/A</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-sm">
                            <PaymentBreakdown
                              admin={admin}
                              isMidMonth={false}
                              paymentFrequency={paymentFrequency}
                            />
                          </td>
                          <td className="py-2 px-3 text-sm">
                            <FifthRunBreakdown summary={summary} />
                          </td>
                          <td className="py-2 px-3 text-xs text-gray-600">
                            {admin?.bankName ? (
                              <div>
                                <div className="font-medium">{admin.bankName}</div>
                                <div>{admin.bankAccountNo}</div>
                                {admin.bankAccountName && (
                                  <div className="text-gray-500">{admin.bankAccountName}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-xs">
                            {(() => {
                              const state = summaryState(summary);
                              const isBusy = busyId === summary.id;
                              // Part-time totals cannot be hand-set: the run net is
                              // gross minus the withholding stored beside it, so an
                              // edited figure would leave the WHT certificate stating
                              // a tax that is not 3% of what was paid. Fix the source
                              // and recalculate instead — the endpoint refuses it too.
                              const isPartTime = summary.admin?.employmentType === 'parttime';
                              return (
                                <div className="flex flex-col gap-1 items-start">
                                  {isPartTime ? (
                                    <span
                                      className="text-gray-400 cursor-not-allowed"
                                      title="Part-time payroll cannot be edited by hand — it would break the WHT certificate. Correct the source data and recalculate."
                                    >
                                      Edit totals
                                    </span>
                                  ) : (
                                    <button
                                      onClick={() => setEditingSummary(summary)}
                                      disabled={isBusy}
                                      className="text-indigo-600 hover:text-indigo-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                                    >
                                      Edit totals
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleRecalculate(summary)}
                                    disabled={isBusy}
                                    className="text-indigo-600 hover:text-indigo-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                                  >
                                    {state === 'manual' ? 'Recalculate (drop edit)' : 'Recalculate'}
                                  </button>
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {editingSummary && (
          <ManualEditModal
            summary={editingSummary}
            isSaving={busyId === editingSummary.id}
            onCancel={() => setEditingSummary(null)}
            onSave={handleSaveManual}
          />
        )}
      </div>
    </div>
  );
};

export default AdminKpiSummaryGenerate;

